import { useMemo, useState } from 'react';
import {
  Bar,
  BarChart,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  Cell
} from 'recharts';
import type { Segment, VipHypeRecord, VipTopicRecord } from '../../types';
import { SEGMENT_COLORS } from '../../constants';
import SegmentToggle from '../common/SegmentToggle';
import { tooltipStyles } from '../../utils/chartStyles';

interface VipHypeChartProps {
  hypeData: Record<Segment, VipHypeRecord[]>;
  topicsData: Record<Segment, Record<string, VipTopicRecord[]>>;
}

const VipHypeChart = ({ hypeData, topicsData }: VipHypeChartProps) => {
  const [segment, setSegment] = useState<Segment>('Left');
  const [selectedVip, setSelectedVip] = useState<string | null>(null);

  const currentHype = useMemo(() => hypeData[segment] || [], [hypeData, segment]);
  const currentTopics = useMemo(() => {
    if (!selectedVip) return [];
    return topicsData[segment]?.[selectedVip] || [];
  }, [topicsData, segment, selectedVip]);

  const handleBarClick = (data: any) => {
    if (data && data.vipName) {
      setSelectedVip(data.vipName);
    }
  };

  const TopicLabel = (props: any) => {
    const { x = 0, y = 0, value } = props;
    return (
      <text
        x={Math.max(12, x)}
        y={y - 6}
        fill="#ffffff"
        fontSize={14}
        textAnchor="start"
      >
        {value}
      </text>
    );
  };

  if (selectedVip) {
    return (
      <div>
        <div className="chart-controls stacked">
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
            <button 
              className="back-button" 
              onClick={() => setSelectedVip(null)}
              style={{
                background: 'rgba(255,255,255,0.1)',
                border: '1px solid rgba(255,255,255,0.2)',
                color: 'white',
                padding: '4px 12px',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              ← Back to Leaders
            </button>
            <h3 style={{ color: 'white', margin: 0, fontSize: '1.1rem' }}>{selectedVip} - Top Topics</h3>
          </div>
          <SegmentToggle value={segment} onChange={setSegment} />
        </div>
        <div className="chart-wrapper">
          <ResponsiveContainer width="100%" height={500}>
            <BarChart
              data={currentTopics}
              layout="vertical"
              margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
              barCategoryGap={30}
            >
              <XAxis type="number" hide />
              <YAxis
                type="category"
                dataKey="representativeTitle"
                width={0}
                tick={false}
                axisLine={false}
              />
              <Tooltip
                {...tooltipStyles}
                cursor={{ fill: 'rgba(255,255,255,0.08)' }}
                labelFormatter={() => ''}
                formatter={(value: any, _name, entry) => [
                  `${Number(value).toLocaleString()} engagement`,
                  (entry?.payload as VipTopicRecord).representativeTitle
                ]}
              />
              <Bar 
                dataKey="engagement" 
                fill={SEGMENT_COLORS[segment]} 
                radius={[0, 10, 10, 0]}
                barSize={20}
              >
                <LabelList dataKey="representativeTitle" content={TopicLabel} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="chart-controls stacked">
        <SegmentToggle value={segment} onChange={setSegment} />
      </div>
      <div className="chart-wrapper">
        <ResponsiveContainer width="100%" height={500}>
          <BarChart
            data={currentHype}
            margin={{ top: 20, right: 30, left: 20, bottom: 100 }}
          >
            <XAxis 
              dataKey="vipName" 
              tick={{ fill: '#ffffff', fontSize: 12, fontWeight: 600 }}
              angle={-45}
              textAnchor="end"
              interval={0}
            />
            <YAxis tick={{ fill: '#94a3b8', fontSize: 12 }} />
            <Tooltip
              {...tooltipStyles}
              cursor={{ fill: 'rgba(255,255,255,0.08)' }}
              formatter={(value: any) => [`${Number(value).toLocaleString()} total hype`, 'Engagement']}
            />
            <Bar 
              dataKey="totalHype" 
              fill={SEGMENT_COLORS[segment]} 
              radius={[10, 10, 0, 0]}
              style={{ cursor: 'pointer' }}
              onClick={(data) => handleBarClick(data)}
            >
              {currentHype.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={SEGMENT_COLORS[segment]} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
        <p style={{ color: '#94a3b8', fontSize: '12px', textAlign: 'center', marginTop: '8px' }}>
          Click on a leader's bar to see their top topics
        </p>
      </div>
    </div>
  );
};

export default VipHypeChart;
